/*
 * Floris London — "A little about you" customer survey.
 *
 * One question per screen. Writes answers to Klaviyo profile properties via the
 * onsite klaviyo.js already loaded by the Klaviyo/Shopify integration. Klaviyo's
 * own Shopify sync then populates the matching `klaviyo` namespace customer
 * metafields. Nothing here talks to the Shopify Admin API.
 *
 * Spec: claude-os/clients/floris/survey/build-spec.md
 *
 * The question set is deliberately held in code rather than section settings.
 * The option labels ARE the stored values, and existing Klaviyo segments match on
 * them exactly — editing one in the theme editor would silently split a segment.
 * Changes here should be made against the spec, not ad hoc.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Question set — 24 questions, fixed linear order.
   *
   * `property` is the Klaviyo profile property name. It must match exactly;
   * see build-spec.md §3. Note the en-dashes in the range labels.
   * ------------------------------------------------------------------ */

  var VALUE_OPTIONS = [
    'Quality of fragrance',
    'Heritage and history',
    'Packaging and presentation',
    'Exclusivity',
    'Range of products',
    'Personalisation',
    'Value for money',
    'Royal warrant',
    'British business',
    'Independent business',
    'Family-run business',
  ];

  var QUESTIONS = [
    {
      property: 'Gender',
      title: 'How do you identify?',
      type: 'single',
      options: ['Man', 'Woman', 'Other', 'Prefer not to say'],
    },
    {
      property: 'Date of Birth',
      title: 'What is your date of birth?',
      type: 'date',
    },
    {
      property: 'Location',
      title: 'Where are you based?',
      type: 'single',
      options: [
        'London',
        'Greater London',
        'Rest of UK',
        'United States',
        'Europe',
        'Rest of the world',
      ],
    },
    {
      property: 'Income Bracket',
      title: 'Which of these best describes your household income?',
      type: 'single',
      options: [
        'Under £40k',
        '£40–70k',
        '£70–100k',
        '£100–150k',
        'Over £150k',
        'Prefer not to say',
      ],
    },
    {
      property: 'Fragrance Relationship',
      title: 'How would you describe your relationship with fragrance?',
      type: 'single',
      options: [
        'I have one signature scent I wear every day',
        'I rotate between a few fragrances',
        'I choose my scent based on occasion or mood',
        "I'm still exploring and finding my style",
      ],
    },
    {
      property: 'Fragrance Count',
      title: 'How many fragrances do you currently own?',
      type: 'single',
      options: ['1', '2-3', '4-6', '7 or more'],
    },
    {
      property: 'Typical Spend',
      title: 'What do you typically spend on a fragrance per bottle?',
      type: 'single',
      options: ['Under £50', '£50–100', '£100–175', '£175–250', 'Over £250'],
    },
    {
      property: 'Other Brands',
      title: 'Which other fragrance brands do you buy from?',
      type: 'text',
    },
    {
      property: 'First Discovery',
      title: 'How did you first discover Floris?',
      type: 'single',
      allowOther: true,
      options: [
        'Visited the Jermyn Street shop',
        'Recommended by family member',
        'Recommended by friend',
        'Recommended by someone else',
        'Gift I received',
        'Online search',
        'Social media',
        'Press or editorial',
      ],
    },
    {
      property: 'Purchases For',
      title: 'Who do you primarily purchase for?',
      type: 'single',
      options: ['Myself', 'As a gift for someone else', 'Both equally'],
    },
    {
      property: 'Giftee Category',
      title: 'If you buy as a gift, for whom?',
      helper: 'Select all that apply',
      type: 'multi',
      allowOther: true,
      options: ['Partner', 'Parent', 'Friend', 'Colleague', 'Child'],
    },
    {
      property: 'Values',
      title: 'What matters most to you when buying from Floris?',
      helper: 'Select top 3',
      type: 'multi',
      minSelect: 3,
      maxSelect: 3,
      options: VALUE_OPTIONS,
    },
    {
      property: 'Top Value',
      title: 'What matters most to you when buying from Floris?',
      helper: 'Select single most important',
      type: 'single',
      allowOther: true,
      options: VALUE_OPTIONS,
    },
    {
      property: '100ml Frequency',
      title: 'How long would you expect a 100ml bottle of fragrance to last?',
      type: 'single',
      allowOther: true,
      options: [
        'Less than 3 months',
        '3–6 months',
        '6–12 months',
        '1–2 years',
        'More than 2 years',
      ],
    },
    {
      property: 'Shop Visits',
      title: 'Have you ever visited our Jermyn Street shop?',
      type: 'single',
      options: [
        'Yes, regularly',
        'Yes, once or twice',
        "No, but I'd like to",
        "No, I'm not interested",
      ],
    },
    {
      property: 'Situation',
      title: 'Which of these best describes your situation?',
      helper: 'Select all that apply',
      type: 'multi',
      allowOther: true,
      options: [
        'Senior professional',
        'Business owner',
        'Creative',
        'Academic',
        'Retired',
        'Full-time parent',
        'Military or ex-military',
        'Public sector',
      ],
    },
    {
      property: 'Other Top Categories',
      title: 'Do you purchase luxury goods regularly across other categories?',
      helper: 'E.g. fashion, watches, food and drink, travel',
      type: 'single',
      options: [
        'Yes, frequently',
        'Yes, occasionally',
        'Floris is one of my few luxury purchases',
      ],
    },
    {
      property: 'Beauty & Fragrance Vendors',
      title: 'Where else do you typically shop for beauty and fragrance?',
      type: 'single',
      options: [
        'Department store (e.g. Harrods, Selfridges)',
        'Specialist fragrance retailer',
        'Direct from a brand',
        'Online marketplace',
        "I don't shop elsewhere",
      ],
    },
    {
      property: 'Spend Levers',
      title: 'What would encourage you to spend more frequently with Floris?',
      helper: 'Select all that apply',
      type: 'multi',
      options: [
        'A loyalty or rewards programme',
        'Personalised fragrance recommendations',
        'Exclusive early access to new products',
        'Bundled sets at a special price',
        'A subscription or replenishment service',
        'Events or in-store experiences',
        'An opportunity to learn more about fragrance from Floris e.g. a fragrance workshop',
        "Nothing — I'm happy as I am",
      ],
    },
    {
      property: 'Communication Preferences',
      title: 'How do you prefer to hear from Floris?',
      helper: 'Your choice does not affect your subscription status',
      type: 'single',
      options: [
        'Email',
        'Telephone',
        'Post',
        'Social media',
        'Messaging platform. I.e. WhatsApp',
        'No preference',
      ],
    },
    {
      property: 'Bespoke Interest',
      title:
        'Would you be interested in a bespoke or personalised fragrance consultation?',
      type: 'single',
      options: [
        "Yes, I've already done one",
        "Yes, I'd love to",
        'Maybe, if I knew more',
        'Maybe, depending on price',
        'Probably not',
      ],
    },
    /* The two skippable questions. See build-spec.md §7 — forcing an answer here
     * produces a column of "n/a" and "no", which reads downstream as data. */
    {
      property: 'Product Wishlist',
      title:
        'Are there any new or discontinued scents you would like to see released in the next 1-3 years?',
      type: 'text',
      optional: true,
    },
    {
      property: 'Open Feedback',
      title: "Anything else you'd like to tell us?",
      type: 'longtext',
      optional: true,
    },
    {
      property: 'NPS Score',
      title: 'How likely are you to recommend Floris London to a friend or colleague?',
      helper: '0 = not at all likely, 10 = extremely likely',
      type: 'nps',
    },
  ];

  var STORAGE_KEY = 'floris:about-you:v1';
  var EVENT_NAME = 'Completed Preference Survey';
  var SURVEY_VERSION = '2026-08-05';

  /* ------------------------------------------------------------------ *
   * State
   * ------------------------------------------------------------------ */

  function loadState() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  }

  function saveState(state) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      /* Private browsing or storage full. The survey still works, it just
       * cannot resume after a refresh. Not worth interrupting the run for. */
    }
  }

  /* ------------------------------------------------------------------ *
   * Klaviyo
   * ------------------------------------------------------------------ */

  /* Klaviyo recommends installing this proxy alongside its async onsite
   * script so calls made before klaviyo.js is ready are queued and replayed. */
  function getKlaviyo() {
    if (window.klaviyo) return window.klaviyo;

    window._klOnsite = window._klOnsite || [];
    try {
      window.klaviyo = new Proxy({}, {
        get: function (_target, method) {
          if (method === 'push') {
            return function () {
              window._klOnsite.push.apply(window._klOnsite, arguments);
            };
          }
          return function () {
            var args = Array.prototype.slice.call(arguments);
            var callback =
              typeof args[args.length - 1] === 'function' ? args.pop() : null;
            return new Promise(function (resolve) {
              window._klOnsite.push(
                [method].concat(args, [function (result) {
                  if (callback) callback(result);
                  resolve(result);
                }])
              );
            });
          };
        },
      });
    } catch (e) {
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push = function () {
        window._klOnsite.push.apply(window._klOnsite, arguments);
      };
    }
    return window.klaviyo;
  }

  function klaviyoCall(method, args) {
    try {
      var client = getKlaviyo();
      if (typeof client[method] === 'function') {
        client[method].apply(client, args);
      } else {
        client.push([method].concat(args));
      }
    } catch (e) {
      /* Never let an analytics failure break the survey. */
    }
  }

  function identify(properties, onComplete) {
    var identity = {};
    Object.keys(properties).forEach(function (key) {
      identity[key === '$email' ? 'email' : key] = properties[key];
    });
    klaviyoCall('identify', onComplete ? [identity, onComplete] : [identity]);
  }

  /* Native date inputs and sessionStorage use ISO (YYYY-MM-DD). The existing
   * Klaviyo property contract stores Date of Birth as DD/MM/YYYY, so convert
   * only at the outbound boundary and keep the resumable UI state untouched. */
  function answersForKlaviyo(answers) {
    var payload = {};
    Object.keys(answers).forEach(function (key) {
      var value = answers[key];
      if (key === 'Date of Birth' && typeof value === 'string') {
        var dateParts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
        if (dateParts) value = dateParts[3] + '/' + dateParts[2] + '/' + dateParts[1];
      }
      payload[key] = value;
    });
    return payload;
  }

  function trackCompletion(answers) {
    var payload = answersForKlaviyo(answers);
    payload['Survey Version'] = SURVEY_VERSION;
    klaviyoCall('track', [EVENT_NAME, payload]);
  }

  /* ------------------------------------------------------------------ *
   * Survey element
   * ------------------------------------------------------------------ */

  var LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

  function AboutYouSurvey(root) {
    this.root = root;
    this.customerEmail = root.dataset.customerEmail || '';
    this.privacyUrl = root.dataset.privacyUrl || '';

    var state = loadState();
    this.answers = state.answers || {};
    this.index = typeof state.index === 'number' ? state.index : -1;

    /* Identity resolves three ways, in priority order:
     *   1. `_kx` on the URL, appended by Klaviyo to email links and consumed by
     *      klaviyo.js itself. Nothing for us to do beyond noting it is present.
     *   2. A logged-in customer, from Liquid.
     *   3. Email captured on the closing screen.
     * Until one of these holds, answers are buffered and nothing is sent. */
    this.identified = false;
    if (this.customerEmail) {
      identify({ $email: this.customerEmail });
      this.identified = true;
    } else if (this.hasExchangeId()) {
      this.identified = true;
    } else if (state.email) {
      identify({ $email: state.email });
      this.identified = true;
    }

    this.mount();
  }

  AboutYouSurvey.prototype.hasExchangeId = function () {
    try {
      return new URLSearchParams(window.location.search).has('_kx');
    } catch (e) {
      return window.location.search.indexOf('_kx=') !== -1;
    }
  };

  AboutYouSurvey.prototype.persist = function () {
    saveState({
      answers: this.answers,
      index: this.index,
      email: this.customerEmail || undefined,
    });
  };

  /* Write as we go, so a drop-out at question 10 still banks ten answers.
   * This mirrors the Typeform's `partial_responses_to_all_integrations`. */
  AboutYouSurvey.prototype.flush = function () {
    if (!this.identified) return;
    var payload = answersForKlaviyo(this.answers);
    if (Object.keys(payload).length > 0) identify(payload);
  };

  AboutYouSurvey.prototype.mount = function () {
    this.root.innerHTML = '';

    this.progress = document.createElement('div');
    this.progress.className = 'about-you__progress';
    this.progress.innerHTML = '<span class="about-you__progress-bar"></span>';

    this.stage = document.createElement('div');
    this.stage.className = 'about-you__stage';
    this.stage.setAttribute('aria-live', 'polite');

    this.root.appendChild(this.progress);
    this.root.appendChild(this.stage);

    this.onKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.onKeydown);

    this.render();
  };

  AboutYouSurvey.prototype.setProgress = function (value) {
    var bar = this.progress.querySelector('.about-you__progress-bar');
    bar.style.width = Math.max(0, Math.min(100, value)) + '%';
    this.progress.hidden = value <= 0;
  };

  AboutYouSurvey.prototype.render = function () {
    if (this.index < 0) return this.renderWelcome();
    if (this.index >= QUESTIONS.length) {
      return this.identified ? this.complete() : this.renderEmailCapture();
    }
    this.renderQuestion(QUESTIONS[this.index]);
  };

  AboutYouSurvey.prototype.renderWelcome = function () {
    this.setProgress(0);
    var privacy = this.privacyUrl
      ? '<p class="about-you__note">We use your preferences to better understand ' +
        'our customers and improve the Floris experience. They are never shared ' +
        'or sold. Read our ' +
        '<a href="' +
        this.privacyUrl +
        '">privacy policy</a>.</p>'
      : '';

    this.stage.innerHTML =
      '<div class="about-you__screen">' +
      '<p class="about-you__eyebrow">Floris London</p>' +
      '<h1 class="about-you__title">A little about you</h1>' +
      '<p class="about-you__lede">Help us get to know you better. ' +
      'Twenty-four questions, about four minutes.</p>' +
      privacy +
      '<button type="button" class="button about-you__advance">Start</button>' +
      '</div>';

    this.stage
      .querySelector('.about-you__advance')
      .addEventListener('click', this.next.bind(this));
  };

  AboutYouSurvey.prototype.renderQuestion = function (question) {
    var number = this.index + 1;
    this.setProgress((this.index / QUESTIONS.length) * 100);

    var helper = question.helper
      ? '<p class="about-you__helper">' + escapeHtml(question.helper) + '</p>'
      : '';

    this.stage.innerHTML =
      '<div class="about-you__screen">' +
      '<p class="about-you__count">' +
      number +
      ' of ' +
      QUESTIONS.length +
      '</p>' +
      '<h2 class="about-you__title">' +
      escapeHtml(question.title) +
      '</h2>' +
      helper +
      '<div class="about-you__input"></div>' +
      '<div class="about-you__actions"></div>' +
      '<p class="about-you__error" role="alert" hidden></p>' +
      '</div>';

    var host = this.stage.querySelector('.about-you__input');
    var actions = this.stage.querySelector('.about-you__actions');

    switch (question.type) {
      case 'single':
      case 'multi':
        this.renderChoices(host, question);
        break;
      case 'nps':
        this.renderScale(host, question);
        break;
      case 'date':
        this.renderDate(host, question);
        break;
      default:
        this.renderText(host, question);
    }

    if (this.index > 0) {
      var back = document.createElement('button');
      back.type = 'button';
      back.className = 'about-you__back';
      back.textContent = 'Back';
      back.addEventListener('click', this.previous.bind(this));
      actions.appendChild(back);
    }

    /* A skippable question gets a visible Skip control. A Next button that
     * silently tolerates an empty answer does not tell the respondent they
     * are allowed to pass. */
    if (question.optional) {
      var skip = document.createElement('button');
      skip.type = 'button';
      skip.className = 'about-you__skip';
      skip.textContent = 'Skip';
      skip.addEventListener(
        'click',
        function () {
          delete this.answers[question.property];
          this.next();
        }.bind(this)
      );
      actions.appendChild(skip);
    }

    var advance = document.createElement('button');
    advance.type = 'button';
    advance.className = 'button about-you__advance';
    advance.textContent =
      this.index === QUESTIONS.length - 1 ? 'Finish' : 'Next';
    advance.addEventListener('click', this.submitCurrent.bind(this));
    actions.appendChild(advance);
  };

  AboutYouSurvey.prototype.renderChoices = function (host, question) {
    var current = this.answers[question.property];
    var selected = question.type === 'multi'
      ? (Array.isArray(current) ? current.slice() : [])
      : current;

    var list = document.createElement('ul');
    list.className = 'about-you__choices';
    list.setAttribute('role', question.type === 'multi' ? 'group' : 'radiogroup');

    question.options.forEach(function (label, i) {
      var item = document.createElement('li');
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'about-you__choice';
      button.dataset.value = label;
      button.setAttribute(
        'role',
        question.type === 'multi' ? 'checkbox' : 'radio'
      );

      var isOn =
        question.type === 'multi'
          ? selected.indexOf(label) !== -1
          : selected === label;
      button.setAttribute('aria-checked', isOn ? 'true' : 'false');

      button.innerHTML =
        '<span class="about-you__key">' +
        (LETTERS[i] || '') +
        '</span><span class="about-you__label">' +
        escapeHtml(label) +
        '</span>';

      button.addEventListener('click', function () {
        if (question.type === 'multi') {
          var at = selected.indexOf(label);
          if (at === -1) {
            if (question.maxSelect && selected.length >= question.maxSelect) return;
            selected.push(label);
          } else {
            selected.splice(at, 1);
          }
          button.setAttribute('aria-checked', at === -1 ? 'true' : 'false');
        } else {
          selected = label;
          Array.prototype.forEach.call(
            list.querySelectorAll('.about-you__choice'),
            function (other) {
              other.setAttribute(
                'aria-checked',
                other === button ? 'true' : 'false'
              );
            }
          );
        }
        this.clearError();
      }.bind(this));

      item.appendChild(button);
      list.appendChild(item);
    }, this);

    host.appendChild(list);

    if (question.allowOther) {
      list.classList.add('about-you__choices--with-other');

      var otherChoice = document.createElement('div');
      otherChoice.className = 'about-you__other-choice';

      var otherKey = document.createElement('span');
      otherKey.className = 'about-you__key';
      otherKey.textContent = LETTERS[question.options.length] || '';
      otherKey.setAttribute('aria-hidden', 'true');

      var other = document.createElement('input');
      other.type = 'text';
      other.className = 'about-you__other';
      other.placeholder = 'Something else';
      other.setAttribute('aria-label', 'Other answer');
      if (
        typeof selected === 'string' &&
        selected &&
        question.options.indexOf(selected) === -1
      ) {
        other.value = selected;
      }
      other.addEventListener('input', this.clearError.bind(this));
      otherChoice.appendChild(otherKey);
      otherChoice.appendChild(other);
      host.appendChild(otherChoice);
    }

    this.readCurrent = function () {
      var typed = host.querySelector('.about-you__other');
      var extra = typed ? typed.value.trim() : '';

      if (question.type === 'multi') {
        var values = selected.slice();
        if (extra) values.push(extra);
        return values;
      }
      /* A single-choice question with a typed "other" prefers the typed value,
       * since typing is the more deliberate act. */
      if (extra) return extra;
      return selected;
    };
  };

  AboutYouSurvey.prototype.renderScale = function (host, question) {
    var current = this.answers[question.property];
    var chosen = typeof current === 'number' ? current : null;

    var scale = document.createElement('div');
    scale.className = 'about-you__scale';
    scale.setAttribute('role', 'radiogroup');

    for (var n = 0; n <= 10; n++) {
      (function (value) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'about-you__scale-point';
        button.textContent = String(value);
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-checked', chosen === value ? 'true' : 'false');
        button.addEventListener('click', function () {
          chosen = value;
          Array.prototype.forEach.call(
            scale.querySelectorAll('.about-you__scale-point'),
            function (other) {
              other.setAttribute(
                'aria-checked',
                other === button ? 'true' : 'false'
              );
            }
          );
          this.clearError();
        }.bind(this));
        scale.appendChild(button);
      }.bind(this))(n);
    }

    host.appendChild(scale);
    this.readCurrent = function () {
      return chosen;
    };
  };

  AboutYouSurvey.prototype.renderDate = function (host, question) {
    var input = document.createElement('input');
    input.type = 'date';
    input.className = 'about-you__date field__input';
    input.setAttribute('aria-label', question.title);
    /* Nobody buying fragrance was born after today, and a date picker that
     * allows next year invites typos. */
    input.max = new Date().toISOString().slice(0, 10);
    if (this.answers[question.property]) {
      input.value = this.answers[question.property];
    }
    input.addEventListener('input', this.clearError.bind(this));
    host.appendChild(input);

    this.readCurrent = function () {
      return input.value || '';
    };
  };

  AboutYouSurvey.prototype.renderText = function (host, question) {
    var input =
      question.type === 'longtext'
        ? document.createElement('textarea')
        : document.createElement('input');
    if (question.type === 'longtext') {
      input.rows = 4;
    } else {
      input.type = 'text';
    }
    input.className = 'about-you__text field__input';
    input.setAttribute('aria-label', question.title);
    if (this.answers[question.property]) {
      input.value = this.answers[question.property];
    }
    input.addEventListener('input', this.clearError.bind(this));
    host.appendChild(input);

    this.readCurrent = function () {
      return input.value.trim();
    };
  };

  /* ------------------------------------------------------------------ *
   * Navigation
   * ------------------------------------------------------------------ */

  AboutYouSurvey.prototype.showError = function (message) {
    var node = this.stage.querySelector('.about-you__error');
    if (!node) return;
    node.textContent = message;
    node.hidden = false;
  };

  AboutYouSurvey.prototype.clearError = function () {
    var node = this.stage.querySelector('.about-you__error');
    if (node) node.hidden = true;
  };

  AboutYouSurvey.prototype.validate = function (question, value) {
    var empty =
      value === null ||
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0);

    if (empty) {
      if (question.optional) return null;
      return 'Please choose an answer before continuing.';
    }

    if (question.minSelect && value.length < question.minSelect) {
      return 'Please select ' + question.minSelect + '.';
    }
    if (question.maxSelect && value.length > question.maxSelect) {
      return 'Please select no more than ' + question.maxSelect + '.';
    }
    return null;
  };

  AboutYouSurvey.prototype.submitCurrent = function () {
    var question = QUESTIONS[this.index];
    var value = this.readCurrent ? this.readCurrent() : null;

    var error = this.validate(question, value);
    if (error) return this.showError(error);

    var empty =
      value === null ||
      value === undefined ||
      value === '' ||
      (Array.isArray(value) && value.length === 0);

    if (empty) {
      delete this.answers[question.property];
    } else {
      this.answers[question.property] = value;
      if (this.identified) {
        var single = {};
        single[question.property] = value;
        identify(answersForKlaviyo(single));
      }
    }

    this.next();
  };

  AboutYouSurvey.prototype.next = function () {
    this.index += 1;
    this.persist();
    this.render();
    this.focusStage();
  };

  AboutYouSurvey.prototype.previous = function () {
    if (this.index <= 0) return;
    this.index -= 1;
    this.persist();
    this.render();
    this.focusStage();
  };

  AboutYouSurvey.prototype.focusStage = function () {
    var scrollBehavior = 'smooth';
    if (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      scrollBehavior = 'auto';
    }
    window.scrollTo({ top: 0, behavior: scrollBehavior });
    var heading = this.stage.querySelector('.about-you__title');
    if (!heading) return;
    heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
  };

  AboutYouSurvey.prototype.handleKeydown = function (event) {
    if (this.index < 0 || this.index >= QUESTIONS.length) return;
    var tag = (event.target.tagName || '').toLowerCase();
    var typing = tag === 'input' || tag === 'textarea';

    if (event.key === 'Enter' && !event.shiftKey) {
      if (typing && tag === 'textarea') return;
      event.preventDefault();
      return this.submitCurrent();
    }

    if (typing) return;

    var question = QUESTIONS[this.index];
    if (question.type !== 'single' && question.type !== 'multi') return;

    var at = LETTERS.indexOf(event.key.toUpperCase());
    if (at === -1 || at >= question.options.length) return;
    var button = this.stage.querySelectorAll('.about-you__choice')[at];
    if (button) {
      event.preventDefault();
      button.click();
    }
  };

  /* ------------------------------------------------------------------ *
   * Closing screens
   * ------------------------------------------------------------------ */

  AboutYouSurvey.prototype.renderEmailCapture = function () {
    this.setProgress(100);
    this.stage.innerHTML =
      '<div class="about-you__screen">' +
      '<h2 class="about-you__title">Enter your email</h2>' +
      '<p class="about-you__lede">Add your email so we can save your preferences.</p>' +
      '<input type="email" class="about-you__text field__input" ' +
      'autocomplete="email" aria-label="Email address" placeholder="you@example.com">' +
      '<div class="about-you__actions">' +
      '<button type="button" class="button about-you__advance">Finish</button>' +
      '</div>' +
      '<p class="about-you__error" role="alert" hidden></p>' +
      '</div>';

    var input = this.stage.querySelector('input');
    this.stage
      .querySelector('.about-you__advance')
      .addEventListener('click', function () {
        var email = input.value.trim();
        if (!email || email.indexOf('@') === -1) {
          return this.showError('Please enter a valid email address.');
        }
        identify({ $email: email });
        this.identified = true;
        this.customerEmail = email;
        this.persist();
        this.complete();
      }.bind(this));
  };

  /* The single completion path. Guarded, because both the identified route and
   * the email-capture route land here, and a duplicate completion event would
   * double-count the metric and could double-trigger any flow keyed on it. */
  AboutYouSurvey.prototype.complete = function () {
    if (!this.completed) {
      this.completed = true;
      if (this.customerEmail) {
        var identity = answersForKlaviyo(this.answers);
        identity.$email = this.customerEmail;
        identify(identity, function () {
          trackCompletion(this.answers);
        }.bind(this));
      } else {
        this.flush();
        trackCompletion(this.answers);
      }
      try {
        window.sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        /* Nothing to clean up. */
      }
    }
    this.renderThankYou();
  };

  AboutYouSurvey.prototype.renderThankYou = function () {
    this.setProgress(100);
    document.removeEventListener('keydown', this.onKeydown);

    this.stage.innerHTML =
      '<div class="about-you__screen about-you__screen--end">' +
      '<p class="about-you__eyebrow">Floris London</p>' +
      '<h2 class="about-you__title">Thank you</h2>' +
      '<p class="about-you__lede">Thank you for taking the time to tell us. ' +
      'It shapes what we send you from here.</p>' +
      '<a class="button" href="/">Continue shopping</a>' +
      '</div>';
  };

  /* ------------------------------------------------------------------ */

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (character) {
      return {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      }[character];
    });
  }

  function init() {
    var roots = document.querySelectorAll('[data-about-you-survey]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.mounted) return;
      root.dataset.mounted = 'true';
      new AboutYouSurvey(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders sections without a page load. */
  document.addEventListener('shopify:section:load', init);
})();
